import {useEffect} from 'react';
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    fetch,
    fetchRecords,
    update,
} from '../services/BillableUnitsService';
import {toOperatorError} from '../services/errors';
import {BillableUnit} from '../types';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

// Structured, module-scoped keys (§8). Every list key sits under `lists()`, so one
// invalidation reaches the table and the rate-plans dropdown alike.
export const billableUnitsKeys = {
    all: ['billable-units'] as const,
    lists: () => [...billableUnitsKeys.all, 'list'] as const,
    list: (scope: 'table' | 'all') => [...billableUnitsKeys.lists(), scope] as const,
    details: () => [...billableUnitsKeys.all, 'detail'] as const,
    detail: (id: string) => [...billableUnitsKeys.details(), id] as const,
};

// Page size is a server concern, fixed here (§9) — never a user control.
const PAGE_SIZE = 10;

// Set deliberately: the default of 0 refetches every loaded page on every mount.
const STALE_TIME = 30_000;

// No `sort` and no keyword are sent: listBillableUnits ignores `sort` (it always
// returns created DESC from record-type-created-index) and its filter has no name
// field. Sorting and keyword search are applied client-side, in useBillableUnits.
const infiniteListOptions = (scope: 'table' | 'all') => ({
    queryKey: billableUnitsKeys.list(scope),
    queryFn: ({pageParam}: { pageParam: string | null }) =>
        fetchRecords({limit: PAGE_SIZE, nextToken: pageParam}),
    getNextPageParam: (lastPage: Awaited<ReturnType<typeof fetchRecords>>) => {
        if (!lastPage || !lastPage.nextToken) return undefined;
        return lastPage.nextToken;
    },
    initialPageParam: null as string | null,
    staleTime: STALE_TIME,
});

export const flattenPages = (pages?: Awaited<ReturnType<typeof fetchRecords>>[]): BillableUnit[] =>
    (pages?.flatMap(page => (page && 'items' in page && page.items) ? page.items : []) ?? [])
        .filter(Boolean);

export const useBillableUnitsInfiniteQuery = () => useInfiniteQuery(infiniteListOptions('table'));

// Every billable unit, for a picker. A select box cannot "load more", and the
// catalog is small and admin-authored, so this pages through to the end — unlike
// the table, which must never fetch all pages (§9).
export const useAllBillableUnitsQuery = () => {
    const query = useInfiniteQuery(infiniteListOptions('all'));
    const {hasNextPage, isFetchingNextPage, fetchNextPage} = query;

    useEffect(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    return {
        items: flattenPages(query.data?.pages),
        loading: query.isLoading || hasNextPage,
    };
};

export const useBillableUnitQuery = (id?: string) => useQuery({
    queryKey: billableUnitsKeys.detail(id ?? ''),
    queryFn: () => fetch(id!),
    enabled: !!id,
    staleTime: STALE_TIME,
});

export const useCreateBillableUnitMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (values: BillableUnit) => add(values),
        onSuccess: () => {
            notifySuccess('Billable Unit created');
            queryClient.invalidateQueries({queryKey: billableUnitsKeys.lists()});
        },
        onError: (error) => notifyError(toOperatorError(error, 'create')),
    });
};

export const useUpdateBillableUnitMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({id, values}: { id: string, values: BillableUnit }) => update(id, values),
        onSuccess: (_, {id}) => {
            notifySuccess('Billable Unit updated');
            queryClient.invalidateQueries({queryKey: billableUnitsKeys.lists()});
            queryClient.invalidateQueries({queryKey: billableUnitsKeys.detail(id)});
        },
        onError: (error) => notifyError(toOperatorError(error, 'update')),
    });
};

export const useDeleteBillableUnitsMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (ids: string[]) => Promise.all(ids.map(id => deleteRecord(id))),
        onSuccess: (_, ids) => {
            notifySuccess(ids.length === 1 ? 'Billable Unit deleted' : `${ids.length} Billable Units deleted`);
            queryClient.invalidateQueries({queryKey: billableUnitsKeys.lists()});
            ids.forEach(id => queryClient.removeQueries({queryKey: billableUnitsKeys.detail(id)}));
        },
        onError: (error) => notifyError(toOperatorError(error, 'delete')),
    });
};
